import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetBalanceSheetResponse } from '@/api/handlers/resources/statements/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { StatementComparative } from '@/api/models';
import { StatementQueryKeys } from './statementQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * The position as of a date. Keyed on `asOf`, `locale` and `comparative`, so
 * changing any of them refetches; `keepPreviousData` holds the prior statement on
 * screen while the new one loads (no flash back to a skeleton on every date
 * change).
 *
 * One request covers the comparative column too — the prior figures, their delta
 * and the window they cover all come back in this payload.
 */
export const useBalanceSheetQuery = (
    asOf: string,
    locale: string,
    comparative: StatementComparative,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetBalanceSheetResponse>>({
        queryKey: StatementQueryKeys.balanceSheet(asOf, locale, comparative),
        queryFn: () =>
            apiClient.statements.getBalanceSheet(asOf, { locale, comparative }),
        enabled: asOf.length > 0,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
};
